import React from 'react';
import { flexRender } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronUpIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import DataTableFilter from './DataTableFilter';

/* eslint-disable react/prop-types */
/**
 * @template {import('@tanstack/react-table').RowData} TData
 * @param {{ table: import('@tanstack/react-table').Table<TData>, enableColumnResizing: boolean, enableColumnFilters: boolean }} props
 */
const DataTableHeader = ({ table, enableColumnResizing, enableColumnFilters, stickyHeader = false, headerClassName = '' }) => (
  <thead
    className={`${headerClassName || 'bg-gray-50 text-l font-bold uppercase tracking-wide text-black'} ${
      stickyHeader ? 'sticky top-0 z-10' : ''
    }`}
  >
    {table.getHeaderGroups().map((headerGroup) => (
      <tr key={headerGroup.id}>
        {headerGroup.headers.map((header) => {
          const sorted = header.column.getIsSorted();
          const canSort = header.column.getCanSort();
          const columnName = header.column.columnDef.header;

          return (
            <th
              key={header.id}
              colSpan={header.colSpan}
              scope="col"
              aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : canSort ? 'none' : undefined}
              className="relative min-w-0 border-b border-gray-200 px-4 py-3 text-left"
              style={{ width: header.getSize() }}
            >
              {header.isPlaceholder ? null : (
                <>
                  <div className="min-w-0">
                    <button
                      type="button"
                      className={`flex max-w-full items-center gap-1 text-left text-inherit disabled:text-inherit disabled:opacity-100 ${canSort ? 'cursor-pointer select-none' : 'cursor-default'}`}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      disabled={!canSort}
                      aria-label={canSort ? `Sort by ${typeof columnName === 'string' ? columnName : header.column.id}` : undefined}
                    >
                      <span className="min-w-0 break-words">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                    {canSort && (
                      <span aria-hidden="true" className="text-black">
                        {sorted === 'asc' ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : sorted === 'desc' ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronUpDownIcon className="h-4 w-4" />
                        )}
                      </span>
                    )}
                    </button>
                  </div>
                  {enableColumnFilters && <DataTableFilter column={header.column} />}
                </>
              )}
              {enableColumnResizing && header.column.getCanResize() && (
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize ${header.column.id} column`}
                  aria-valuenow={header.getSize()}
                  tabIndex={0}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                    header.getResizeHandler()(event);
                  }}
                  onTouchStart={(event) => {
                    event.stopPropagation();
                    header.getResizeHandler()(event);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                    event.preventDefault();
                    const direction = event.key === 'ArrowRight' ? 1 : -1;
                    const column = header.column;
                    const minSize = column.columnDef.minSize ?? 20;
                    const maxSize = column.columnDef.maxSize ?? Number.MAX_SAFE_INTEGER;
                    table.setColumnSizing((current) => ({
                      ...current,
                      [column.id]: Math.min(maxSize, Math.max(minSize, column.getSize() + direction * 10)),
                    }));
                  }}
                  title="Drag to resize column"
                  className="absolute right-[-4px] top-0 z-10 h-full w-2 cursor-col-resize touch-none select-none bg-transparent after:absolute after:inset-y-0 after:left-[3px] after:w-px after:bg-transparent after:content-[''] hover:after:bg-brand/60 focus-visible:outline-none focus-visible:after:bg-brand"
                />
              )}
            </th>
          );
        })}
      </tr>
    ))}
  </thead>
);

export default DataTableHeader;
